import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Stack
} from "@mui/material";
import { formatDateTime } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import FetchWorkstationsControl from "~/components/FetchWorkstationsControl";
import SignalRConnectionIndicator from "~/components/SignalRConnectionIndicator";
import { selectPongs } from "~/features/andon/andon.slice";
import { useWorkstationPolling } from "~/hooks/useWorkstationPolling";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import PongControls from "./PongControls";

export default function WorkstationDashboard() {
  useWorkstationPolling();
  const { t } = useTranslation();
  const pongs = useAppSelector(selectPongs);
  const { data: workstations = [] } = useGetWorkstationsQuery();

  // Combine workstation data with pong data - only include workstations with pong data
  const workstationData = workstations
    .map((workstation) => {
      const pong = pongs[workstation.id];
      const pongData = pong?.pongData;

      return {
        workstationId: workstation.id,
        deviceId: workstation.deviceId,
        userId: workstation.userId
          ? extractUserNameFromAuth0Id(workstation.userId)
          : "No User",
        networkType: pongData?.network?.effectiveType || "Unknown",
        downlink: pongData?.network?.downlink || 0,
        rtt: pongData?.network?.rtt || 0,
        os: pongData?.userAgentParsed?.os || "Unknown",
        browser: pongData?.userAgentParsed?.browser || "Unknown",
        browserVersion: pongData?.userAgentParsed?.version || "Unknown",
        screenHeight: pongData?.screen?.height || 0,
        screenWidth: pongData?.screen?.width || 0,
        timePingSent: pong?.timePingSent || null,
        isOnline: !!pong // Has recent pong data
      };
    })
    .filter((workstation) => workstation.isOnline); // Only show workstations with pong data

  const getNetworkTypeColor = (networkType: string) => {
    switch (networkType.toLowerCase()) {
      case "4g":
        return "success";
      case "3g":
        return "warning";
      case "slow-2g":
      case "2g":
        return "error";
      default:
        return undefined; // No color = default styling
    }
  };

  const getConnectionColor = (rtt: number) => {
    if (rtt === 0) return undefined; // No color = default styling
    if (rtt <= 50) return "success";
    if (rtt <= 150) return "warning";
    return "error";
  };

  return (
    <Stack spacing={2}>
      {/* Controls */}
      <Stack direction="row" alignItems="center" spacing={3}>
        <PongControls />
        <FetchWorkstationsControl />
        <SignalRConnectionIndicator />
      </Stack>

      {/* Summary - at top */}
      {workstationData.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            Workstations with data: {workstationData.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total workstations: {workstations.length}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("device id")}</TableCell>
              <TableCell>{t("user")}</TableCell>
              <TableCell>{t("status")}</TableCell>
              <TableCell>Network Type</TableCell>
              <TableCell>Downlink (Mbps)</TableCell>
              <TableCell>RTT (ms)</TableCell>
              <TableCell>OS</TableCell>
              <TableCell>Browser</TableCell>
              <TableCell>Screen Size</TableCell>
              <TableCell>Last Ping</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workstationData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary">
                    No workstations with data found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              workstationData.map((workstation) => (
                <TableRow key={workstation.workstationId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {workstation.deviceId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {workstation.workstationId}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {workstation.userId}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip label="Online" color="success" size="small" />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={workstation.networkType}
                      color={getNetworkTypeColor(workstation.networkType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {workstation.downlink > 0
                        ? workstation.downlink.toFixed(1)
                        : "—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={workstation.rtt > 0 ? `${workstation.rtt}` : "—"}
                      color={getConnectionColor(workstation.rtt)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{workstation.os}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {workstation.browser}
                    </Typography>
                    {workstation.browserVersion && (
                      <Typography variant="caption" color="text.secondary">
                        v{workstation.browserVersion}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {workstation.screenWidth > 0 &&
                      workstation.screenHeight > 0
                        ? `${workstation.screenWidth} × ${workstation.screenHeight}`
                        : "—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {workstation.timePingSent
                        ? formatDateTime(new Date(workstation.timePingSent))
                        : "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
